// M004-F0646 / M004-F0647 — the users-drawer "Price plans" section.
// Design: docs/design/M004-F0646-F0649-Users-Drawer-Price-Plans-and-Booking-Price.md §2-§3
// (clickable reference: docs/design/assets/M004-F0646-price-plans-mock.html).
//
// Contract: Core M005-D004 §10.12-§10.15 (Core MR !938). Consumer rules:
//   * NO rate arithmetic - the coverage strip, impact line and amounts are
//     Core's; this component only formats them.
//   * Permission gating is by the EFFECTIVE my-permissions projection
//     (pricing_plans_view / pricing_member_plan_assign), never user.role.
//   * Nothing is written until Save. Save = POST /assignments/batch (adds),
//     then PATCH (end-date) or DELETE (never-effective) per removal, all
//     through ONE savePlanChanges() that a SAL-4 step-up REPLAYS with the
//     same draft (the Placeholders.js:1332 / LedgerAccountPage.js:169 idiom).
//   * Server sentences (`message`) print verbatim (P-3); never a bare code.
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import StepUpDialog from '../../auth/StepUpDialog';
import { getStepUpChallengeFromError } from '../../../utils/stepUp';
import { getMyEffectiveOrganisationPermissions, pricingApi, roomApi } from '../../../services/api';
import { pricePlanApi } from '../../../services/pricePlanApi';
import { normaliseAssignment, normalisePlan } from '../../../utils/priceWire';
import AddPlanPicker from './AddPlanPicker';
import CoverageStrip from './CoverageStrip';
import PlanCard, { BaseCard } from './PlanCard';
import RemoveConfirm from './RemoveConfirm';
import {
  BASE_STYLE, addDaysIso, buildPickerPlans, buildPlanStyles, countDraftChanges, emptyDraft,
  groupAssignments, impactBodyFor, isNeverEffective, localIsoDate, serverMessage,
} from './planModel';
import './MemberPricePlans.css';

export const MEMBER_PRICE_PLANS_TESTID = 'admin-user-price-plans';

// Core's tinyint columns (is_public_default, is_template) are JSON 0/1, not booleans.
const isTruthyFlag = (value) => value === true || value === 1 || value === '1';

const unwrap = (response) => response?.data?.data ?? response?.data ?? {};

let tempCounter = 0;
const nextTempId = () => { tempCounter += 1; return `new-${tempCounter}`; };

const MemberPricePlansSection = ({ userId, userName, memberships }) => {
  const today = useMemo(() => localIsoDate(), []);
  const orgOptions = useMemo(() => (memberships || []).map((m) => ({
    id: String(m.organisationId), name: m.organisationName || `Organisation ${m.organisationId}`,
  })), [memberships]);
  const [orgId, setOrgId] = useState('');
  const [perms, setPerms] = useState(null); // null = loading
  const [assignments, setAssignments] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [plans, setPlans] = useState([]);
  const [plansLoaded, setPlansLoaded] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [roomId, setRoomId] = useState('');
  const [asOf, setAsOf] = useState(today);
  const [coverage, setCoverage] = useState(null);
  const [coverageError, setCoverageError] = useState('');
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [afterCoverage, setAfterCoverage] = useState(null);
  const [draft, setDraft] = useState(emptyDraft());
  const [pickerOpen, setPickerOpen] = useState(false);
  const [confirming, setConfirming] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [notice, setNotice] = useState('');
  const [stepUp, setStepUp] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);

  const draftRef = useRef(draft);
  draftRef.current = draft;
  const progressRef = useRef({});
  const assignmentsRef = useRef(assignments);
  assignmentsRef.current = assignments;

  // The selected organisation follows the membership list (a one-org user has
  // no selector; a multi-org user gets one).
  useEffect(() => {
    if (!orgId && orgOptions.length > 0) setOrgId(orgOptions[0].id);
    if (orgId && orgOptions.length > 0 && !orgOptions.some((o) => o.id === orgId)) setOrgId(orgOptions[0].id);
  }, [orgOptions, orgId]);

  // 1. effective permissions for the selected organisation
  useEffect(() => {
    let cancelled = false;
    setPerms(null);
    setDraft(emptyDraft());
    setConfirming(null);
    setPickerOpen(false);
    setNotice('');
    setSaveError('');
    progressRef.current = {};
    if (!orgId) return undefined;
    getMyEffectiveOrganisationPermissions(orgId).then((permissions) => {
      if (!cancelled) setPerms(permissions || {});
    });
    return () => { cancelled = true; };
  }, [orgId, userId]);

  const canView = perms?.pricing_plans_view === true;
  const canAssign = perms?.pricing_member_plan_assign === true;

  // 2. assignments + plans + rooms
  useEffect(() => {
    let cancelled = false;
    if (!orgId || !canView || !userId) return undefined;
    setLoadError('');
    pricingApi.getMemberPlanAssignments(orgId, userId)
      .then((response) => { if (!cancelled) setAssignments((unwrap(response).assignments || []).map(normaliseAssignment)); })
      .catch((error) => { if (!cancelled) setLoadError(serverMessage(error, 'Could not load this person\'s price plans.')); });
    return () => { cancelled = true; };
  }, [orgId, userId, canView, reloadTick]);

  useEffect(() => {
    let cancelled = false;
    if (!orgId || !canView) return undefined;
    setPlansLoaded(false);
    Promise.all([
      pricingApi.getPlans(orgId).catch(() => null),
      roomApi.getRooms(orgId, true, false).catch(() => null),
    ]).then(([plansResponse, roomsResponse]) => {
      if (cancelled) return;
      const body = plansResponse ? unwrap(plansResponse) : {};
      setPlans((body.plans || (Array.isArray(body) ? body : [])).map(normalisePlan));
      const roomBody = roomsResponse ? unwrap(roomsResponse) : [];
      const roomRows = Array.isArray(roomBody) ? roomBody : (roomBody.rooms || []);
      setRooms(roomRows.map((room) => ({ id: room.id, name: room.name })));
      setPlansLoaded(true);
    });
    return () => { cancelled = true; };
  }, [orgId, canView]);

  const roomNames = useMemo(() => {
    const names = {};
    rooms.forEach((room) => { names[String(room.id)] = room.name; });
    assignments.forEach((a) => { if (a.plan?.room) names[String(a.plan.room.id)] = a.plan.room.name; });
    return names;
  }, [rooms, assignments]);

  const liveGroups = useMemo(() => groupAssignments(assignments, today), [assignments, today]);
  const pickerPlans = useMemo(() => buildPickerPlans(plans, roomNames), [plans, roomNames]);

  // Room choices for the strip: rooms where the person has (or is adding) a
  // plan; otherwise every room so there is always something to look at.
  const roomChoices = useMemo(() => {
    const ids = new Map();
    liveGroups.forEach((g) => g.rooms.forEach((r) => { if (r.roomId !== null) ids.set(String(r.roomId), r.roomName); }));
    draft.adds.forEach((add) => add.rooms.forEach((r) => ids.set(String(r.roomId), r.roomName)));
    if (ids.size === 0) rooms.forEach((r) => ids.set(String(r.id), r.name));
    return [...ids.entries()].map(([id, name]) => ({ id, name }));
  }, [liveGroups, draft.adds, rooms]);

  useEffect(() => {
    if (roomChoices.length > 0 && !roomChoices.some((r) => r.id === String(roomId))) {
      setRoomId(roomChoices[0].id);
    }
  }, [roomChoices, roomId]);

  // 3. coverage (server evaluated) for room + as-of
  useEffect(() => {
    let cancelled = false;
    if (!orgId || !canView || !userId || !roomId) { setCoverage(null); return undefined; }
    setCoverageLoading(true);
    setCoverageError('');
    pricePlanApi.getMemberCoverage(orgId, userId, { room_id: roomId, as_of: asOf })
      .then((response) => { if (!cancelled) setCoverage(unwrap(response)); })
      .catch((error) => {
        if (!cancelled) { setCoverage(null); setCoverageError(serverMessage(error, 'Could not load the weekly coverage.')); }
      })
      .finally(() => { if (!cancelled) setCoverageLoading(false); });
    return () => { cancelled = true; };
  }, [orgId, userId, roomId, asOf, canView, reloadTick]);

  const removedAssignments = useMemo(() => {
    const ids = new Set(draft.removals.flatMap((r) => r.assignmentIds.map(String)));
    return assignments.filter((a) => ids.has(String(a.id)));
  }, [draft.removals, assignments]);

  // 4. before/after preview whenever the draft changes the candidate set
  const previewKey = JSON.stringify([draft.adds.map((a) => [a.planIds, a.effectiveFrom]), draft.removals.map((r) => r.assignmentIds), roomId]);
  useEffect(() => {
    let cancelled = false;
    const hasSetChange = draft.adds.length > 0 || draft.removals.length > 0;
    if (!hasSetChange || !orgId || !userId || !roomId || !canView) { setAfterCoverage(null); return undefined; }
    const body = {
      ...impactBodyFor({
        userId, draftAdds: draft.adds, endAssignments: removedAssignments, today,
      }),
      room_id: Number(roomId),
    };
    pricePlanApi.previewMemberCoverage(orgId, userId, body)
      .then((response) => { if (!cancelled) setAfterCoverage(unwrap(response)); })
      .catch((error) => {
        if (!cancelled) { setAfterCoverage(null); setCoverageError(serverMessage(error, 'Could not preview the change.')); }
      });
    return () => { cancelled = true; };
    // previewKey captures the candidate-set inputs
  }, [previewKey, orgId, userId, canView]);

  const basePlan = useMemo(() => plans.find((p) => isTruthyFlag(p.is_public_default) && String(p.room_id) === String(roomId)) || null, [plans, roomId]);
  const styles = useMemo(() => {
    const ids = [
      ...assignments.map((a) => a.pricing_plan_id),
      ...draft.adds.flatMap((a) => a.planIds),
      ...(coverage?.plans || []).map((p) => p.id),
    ];
    return buildPlanStyles(ids, basePlan?.id ?? null);
  }, [assignments, draft.adds, coverage, basePlan]);

  const baseRateMinor = useMemo(() => {
    if (Number.isFinite(basePlan?.first_hour_rate_minor)) return basePlan.first_hour_rate_minor;
    if (!basePlan || !coverage) return null;
    // Read (not compute) the base plan's own amount from any covered slot.
    for (const day of coverage.days || []) {
      for (const slot of day.slots || []) {
        const hit = (slot.applicable || []).find((entry) => entry.plan_id === basePlan.id);
        if (hit) return hit.amount_minor;
      }
    }
    return null;
  }, [basePlan, coverage]);

  const roomsWithoutDefault = useMemo(() => {
    if (!plansLoaded) return [];
    const withDefault = new Set(plans.filter((p) => isTruthyFlag(p.is_public_default)).map((p) => String(p.room_id)));
    return rooms.map((r) => String(r.id)).filter((id) => !withDefault.has(id));
  }, [plans, rooms, plansLoaded]);

  // ---- draft actions ---------------------------------------------------------
  const addFromPicker = ({ row, rooms: chosen, effectiveFrom }) => {
    setDraft((prev) => ({
      ...prev,
      adds: [...prev.adds, {
        tempId: nextTempId(),
        key: row.key,
        name: row.name,
        plan: row.plan,
        rateSummary: row.plan.rate_summary || (Number.isFinite(row.firstHourMinor)
          ? { first_hour_minor: row.firstHourMinor, long_hour_minor: row.plan.long_hour_rate_minor ?? row.firstHourMinor, threshold_minutes: row.plan.threshold_minutes ?? 180 }
          : null),
        planIds: chosen.map((r) => r.planId),
        rooms: chosen,
        effectiveFrom,
      }],
    }));
    setNotice(`${row.name} added. Save to apply.`);
    setSaveError('');
  };

  const removeDraftAdd = (tempId) => setDraft((prev) => ({ ...prev, adds: prev.adds.filter((a) => a.tempId !== tempId) }));
  const removeRoomFromAdd = (tempId, planId) => setDraft((prev) => ({
    ...prev,
    adds: prev.adds.map((a) => (a.tempId === tempId
      ? { ...a, planIds: a.planIds.filter((id) => id !== planId), rooms: a.rooms.filter((r) => r.planId !== planId) }
      : a)),
  }));

  const startRemove = async (group) => {
    const assignmentIds = group.items.map((i) => i.id);
    setConfirming({
      groupKey: group.key, group, assignmentIds, impact: null, impactError: '', loading: true, afterCoverage: null, grandfather: false,
    });
    const endAssignments = group.items;
    const body = impactBodyFor({
      userId, draftAdds: [], endAssignments, today,
    });
    try {
      const [impactResponse, previewResponse] = await Promise.all([
        pricePlanApi.assignmentImpact(orgId, body),
        pricePlanApi.previewMemberCoverage(orgId, userId, { ...body, room_id: Number(roomId) }),
      ]);
      setConfirming((prev) => (prev && prev.groupKey === group.key
        ? { ...prev, impact: unwrap(impactResponse), afterCoverage: unwrap(previewResponse), loading: false } : prev));
    } catch (error) {
      setConfirming((prev) => (prev && prev.groupKey === group.key
        ? { ...prev, impactError: serverMessage(error, 'Could not check the impact of removing this plan, so it cannot be removed right now.'), loading: false } : prev));
    }
  };

  const confirmRemove = () => {
    if (!confirming) return;
    setDraft((prev) => ({
      ...prev,
      removals: [...prev.removals, {
        groupKey: confirming.groupKey, assignmentIds: confirming.assignmentIds, grandfather: confirming.grandfather,
      }],
    }));
    setConfirming(null);
    setNotice('Will be removed on Save. Undo any time before then.');
  };

  const undoRemove = (groupKey) => setDraft((prev) => ({ ...prev, removals: prev.removals.filter((r) => r.groupKey !== groupKey) }));

  const changeDate = (group, value) => {
    setDraft((prev) => {
      const dateChanges = { ...prev.dateChanges };
      group.items.forEach((item) => {
        if (!value || value === item.effective_from) delete dateChanges[item.id];
        else dateChanges[item.id] = value;
      });
      return { ...prev, dateChanges };
    });
  };

  const discard = () => {
    setDraft(emptyDraft());
    setConfirming(null);
    setSaveError('');
    progressRef.current = {};
    setNotice('Changes discarded.');
  };

  // ---- Save (ONE function; the step-up REPLAYS it) --------------------------
  const savePlanChanges = useCallback(async () => {
    const current = draftRef.current;
    const progress = progressRef.current;
    setSaving(true);
    setSaveError('');
    try {
      const items = current.adds.flatMap((add) => add.planIds.map((planId) => ({
        pricing_plan_id: planId, effective_from: add.effectiveFrom,
      })));
      if (items.length > 0 && !progress.batch) {
        await pricePlanApi.batchAssignMemberPlans(orgId, { user_id: userId, items });
        progress.batch = true;
      }
      for (const removal of current.removals) {
        for (const assignmentId of removal.assignmentIds) {
          const stepKey = `end:${assignmentId}`;
          if (progress[stepKey]) continue;
          const assignment = assignmentsRef.current.find((a) => String(a.id) === String(assignmentId));
          if (assignment && isNeverEffective(assignment, today)) {
            await pricePlanApi.deleteAssignment(orgId, assignmentId);
          } else {
            const body = { effective_to: addDaysIso(today, -1) };
            if (removal.grandfather) body.grandfather = true;
            await pricePlanApi.patchAssignment(orgId, assignmentId, body);
          }
          progress[stepKey] = true;
        }
      }
      for (const [assignmentId, value] of Object.entries(current.dateChanges)) {
        const stepKey = `date:${assignmentId}`;
        if (progress[stepKey]) continue;
        await pricePlanApi.patchAssignment(orgId, assignmentId, { effective_from: value });
        progress[stepKey] = true;
      }
      progressRef.current = {};
      setDraft(emptyDraft());
      setConfirming(null);
      setPickerOpen(false);
      setNotice('Saved. Upcoming bookings are repriced by Core.');
      setReloadTick((tick) => tick + 1);
    } catch (error) {
      const challenge = getStepUpChallengeFromError(error);
      if (challenge?.id) {
        setStepUp({ challenge });
        setSaveError('A fresh step-up (SAL 4) is needed before this can be saved. Your changes are kept.');
      } else {
        setSaveError(serverMessage(error, 'Could not save the price plan changes.'));
      }
    } finally {
      setSaving(false);
    }
  }, [orgId, userId, today]);

  const completeStepUp = async () => {
    setStepUp(null);
    await savePlanChanges();
  };

  // A draft edit after a partial failure invalidates the recorded progress.
  useEffect(() => { progressRef.current = {}; }, [previewKey, draft.dateChanges]);

  // ---- render ---------------------------------------------------------------
  if (!orgId) return null;
  if (perms === null) {
    return (
      <section className="mpp-panel" data-testid={MEMBER_PRICE_PLANS_TESTID} aria-busy="true">
        <h3>Price plans</h3>
        <div className="mpp-skeleton" data-testid="price-plans-loading" />
      </section>
    );
  }
  if (!canView) return null; // denied: the section is absent (design §3.1)

  const changeCount = countDraftChanges(draft);
  const removingKeys = new Set(draft.removals.map((r) => r.groupKey));
  const assignedPlanIds = new Set([
    ...assignments.filter((a) => groupAssignments([a], today).length > 0).map((a) => String(a.pricing_plan_id)),
    ...draft.adds.flatMap((a) => a.planIds.map(String)),
  ]);
  const orgName = orgOptions.find((o) => o.id === orgId)?.name || '';
  const roomName = roomChoices.find((r) => r.id === String(roomId))?.name || '';
  const hasAnyPlan = liveGroups.length > 0 || draft.adds.length > 0;

  const newPlanHref = `/app/bookings?tab=price-plans&org=${encodeURIComponent(orgId)}&returnTo=${encodeURIComponent(`/app/admin/users?user=${userId}`)}`;

  return (
    <section className="mpp-panel" data-testid={MEMBER_PRICE_PLANS_TESTID} aria-label="Price plans">
      <div className="mpp-head">
        <div>
          <h3>Price plans</h3>
          <p className="mpp-muted">
            {orgName} {'—'} what {userName || 'this person'} pays per hour. The cheapest plan that applies to a booking wins.
          </p>
        </div>
        {canAssign ? (
          <button
            type="button"
            className="mpp-btn mpp-btn-outline mpp-btn-sm"
            aria-expanded={pickerOpen}
            onClick={() => setPickerOpen((v) => !v)}
            data-testid="price-plans-add"
          >
            + Add plan
          </button>
        ) : null}
      </div>

      {orgOptions.length > 1 ? (
        <label className="mpp-row mpp-gap mpp-muted">
          Organisation
          <select value={orgId} onChange={(event) => setOrgId(event.target.value)} data-testid="price-plans-org-select">
            {orgOptions.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </label>
      ) : null}

      {!canAssign ? (
        <p className="mpp-muted" data-testid="price-plans-readonly" title="You need the member plan permission.">
          View only {'—'} you need the member plan permission to change these.
        </p>
      ) : null}
      {loadError ? <div className="mpp-error" role="alert" data-testid="price-plans-load-error">{loadError}</div> : null}
      {notice ? <div className="mpp-notice" role="status" data-testid="price-plans-notice">{notice}</div> : null}

      {pickerOpen && canAssign ? (
        <AddPlanPicker
          pickerPlans={pickerPlans}
          styles={styles}
          rooms={rooms}
          alreadyAssignedPlanIds={assignedPlanIds}
          defaultFrom={today}
          loading={!plansLoaded}
          onAdd={addFromPicker}
          onClose={() => setPickerOpen(false)}
          newPlanHref={newPlanHref}
        />
      ) : null}

      <div data-testid="price-plan-cards">
        <BaseCard baseStyle={BASE_STYLE} rateMinor={baseRateMinor} roomName={roomName} />
        {liveGroups.map((group) => {
          const pending = removingKeys.has(group.key);
          const dirtyDate = group.items.some((item) => draft.dateChanges[item.id]);
          return (
            <React.Fragment key={group.key}>
              <PlanCard
                group={{ ...group, planId: group.planIds[0] }}
                styles={styles}
                today={today}
                canEdit={canAssign}
                pendingRemoval={pending}
                dirtyDate={dirtyDate}
                dateValue={draft.dateChanges[group.items[0].id]}
                noRoomDefaultRoomIds={roomsWithoutDefault}
                onRemove={() => startRemove(group)}
                onUndo={() => undoRemove(group.key)}
                onDateChange={(value) => changeDate(group, value)}
              />
              {confirming && confirming.groupKey === group.key ? (
                <RemoveConfirm
                  name={group.name}
                  roomName={roomName}
                  beforeCoverage={coverage}
                  afterCoverage={confirming.afterCoverage}
                  styles={styles}
                  impact={confirming.impact}
                  impactError={confirming.impactError}
                  impactLoading={confirming.loading}
                  grandfather={confirming.grandfather}
                  onGrandfather={(value) => setConfirming((prev) => ({ ...prev, grandfather: value }))}
                  onConfirm={confirmRemove}
                  onCancel={() => setConfirming(null)}
                />
              ) : null}
            </React.Fragment>
          );
        })}
        {draft.adds.map((add) => (
          <PlanCard
            key={add.tempId}
            group={{
              key: add.tempId,
              name: add.name,
              plan: add.plan,
              planIds: add.planIds,
              rooms: add.rooms.map((r) => ({ planId: r.planId, roomId: r.roomId, roomName: r.roomName })),
              effectiveFrom: add.effectiveFrom,
              effectiveTo: null,
              rateSummary: add.rateSummary,
              redundant: false,
            }}
            styles={styles}
            today={today}
            canEdit={canAssign}
            isNew
            noRoomDefaultRoomIds={roomsWithoutDefault}
            onRemove={() => removeDraftAdd(add.tempId)}
            onRemoveRoom={(room) => removeRoomFromAdd(add.tempId, room.planId)}
          />
        ))}
        {!hasAnyPlan && !loadError ? (
          <p className="mpp-muted" data-testid="price-plans-empty">
            No personal plans {'—'} this person pays studio rates.
          </p>
        ) : null}
      </div>

      <div className="mpp-row mpp-between mpp-strip-head-row">
        <strong>Weekly coverage</strong>
        <div className="mpp-row mpp-gap">
          <label className="mpp-muted" htmlFor="mpp-room">Room</label>
          <select id="mpp-room" value={roomId} onChange={(event) => setRoomId(event.target.value)} data-testid="coverage-room-select">
            {roomChoices.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
          <label className="mpp-muted" htmlFor="mpp-asof">as of</label>
          <input id="mpp-asof" type="date" value={asOf} onChange={(event) => setAsOf(event.target.value || today)} data-testid="coverage-asof" />
        </div>
      </div>
      <CoverageStrip
        coverage={coverage}
        afterCoverage={afterCoverage}
        styles={styles}
        loading={coverageLoading}
        error={coverageError}
      />

      {canAssign && (changeCount > 0 || saveError) ? (
        <div className="mpp-dirtybar" data-testid="price-plans-dirtybar">
          <span aria-live="polite" data-testid="price-plans-dirty-count">
            {changeCount > 0 ? `${changeCount} change${changeCount === 1 ? '' : 's'}` : 'No changes'}
          </span>
          <div className="mpp-row mpp-gap">
            <button type="button" className="mpp-btn mpp-btn-outline" onClick={discard} disabled={saving || changeCount === 0} data-testid="price-plans-discard">Discard</button>
            <button type="button" className="mpp-btn mpp-btn-primary" onClick={savePlanChanges} disabled={saving || changeCount === 0} data-testid="price-plans-save">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
          {saveError ? <div className="mpp-error mpp-dirtybar-error" role="alert" data-testid="price-plans-save-error">{saveError}</div> : null}
        </div>
      ) : null}

      <StepUpDialog
        challenge={stepUp?.challenge || null}
        title="Confirm it's you"
        description="Changing a person's price plans needs a fresh step-up (SAL 4). Your save will replay automatically once verified."
        onComplete={completeStepUp}
        onCancel={() => { setStepUp(null); setSaveError('Step-up cancelled. Nothing was saved; your changes are kept.'); }}
      />
    </section>
  );
};

export default MemberPricePlansSection;
